import React from 'react';
import styled from '@emotion/styled';
import { Box, Center, Flex, IconButton, Palette, Text, Button } from '@updater/ui-uds';
import { X, Microphone, Stop } from '@phosphor-icons/react';

const Overlay = styled(Box)`
  position: fixed;
  inset: 0;
  background: radial-gradient(60% 60% at 50% 40%, rgba(0, 156, 252, 0.16) 0%, rgba(0, 0, 0, 0.92) 100%),
              linear-gradient(180deg, rgba(0, 8, 93, 0.36) 0%, rgba(0, 156, 252, 0.36) 36%, rgba(0, 8, 93, 0.36) 77%);
  backdrop-filter: blur(12px);
  color: ${Palette.white};
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const HeaderBar = styled(Flex)`
  padding: 1rem 1.25rem;
  align-items: center;
  justify-content: space-between;
`;

const Orb = styled(Box)<{ $isRecording: boolean }>`
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(50% 50% at 50% 50%, #00A9FF 0%, #0085D7 60%, #0065A8 100%);
  box-shadow: 0 0 40px rgba(0, 156, 252, 0.6), inset 0 0 24px rgba(255, 255, 255, 0.25);
  display: block;
  margin: 0 auto;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: -16px;
    border-radius: 50%;
    background: radial-gradient(60% 60% at 50% 50%, rgba(0, 156, 252, 0.25), rgba(0, 156, 252, 0));
    opacity: ${(props: { $isRecording: boolean }) => (props.$isRecording ? 1 : 0)};
    transform: scale(${(props: { $isRecording: boolean }) => (props.$isRecording ? 1 : 0.92)});
    animation: ${(props: { $isRecording: boolean }) => (props.$isRecording ? 'orbPulse 1.8s ease-in-out infinite' : 'none')};
    pointer-events: none;
  }

  @keyframes orbPulse {
    0% { transform: scale(0.92); opacity: 0.6; }
    50% { transform: scale(1.04); opacity: 1; }
    100% { transform: scale(0.92); opacity: 0.6; }
  }
`;

const BottomBar = styled(Flex)`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 1rem;
  padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 1rem);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.38) 80%);
`;

const MicButton = styled(Button)`
  width: 78px;
  height: 78px;
  border-radius: 9999px;
  background: ${Palette.white};
  color: ${Palette.royalBlue};
  box-shadow: 0 8px 28px rgba(0, 156, 252, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  margin-bottom: 0.5rem;
`;

type VoiceAgentScreenProps = {
  onClose: () => void;
};

export const VoiceAgentScreen: React.FC<VoiceAgentScreenProps> = ({ onClose }: VoiceAgentScreenProps) => {
  const [isRecording, setIsRecording] = React.useState(false);
  const mediaStreamRef = React.useRef<MediaStream | null>(null);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const chunksRef = React.useRef<Blob[]>([]);

  const stopRecording = React.useCallback((): void => {
    setIsRecording(false);
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      // ignore stop errors if already stopped
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((t: MediaStreamTrack) => t.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const startRecording = React.useCallback(async () => {
    if (isRecording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const audioBlob: Blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        // TODO: send audioBlob to speech-to-text when backend is available
        const url = URL.createObjectURL(audioBlob);
        URL.revokeObjectURL(url);
        chunksRef.current = [];
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Microphone permission or recording failed', err);
      setIsRecording(false);
    }
  }, [isRecording]);

  React.useEffect(() => {
    return () => {
      stopRecording();
    };
  }, [stopRecording]);

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      void startRecording();
    }
  };

  return (
    <Overlay role="dialog" aria-modal="true" aria-label="Voice agent">
      <HeaderBar>
        {/* @ts-expect-error FIXME: TS2322 - strict mode migration */}
        <Text variant="l" weight="bold" style={{ color: Palette.white }}>Updater2.0</Text>
        <IconButton
          aria-label="Close voice agent"
          onClick={onClose}
          variant="transparent"
          icon={<X size={22} color={Palette.white} />}
        />
      </HeaderBar>

      <Center vertical flex="1">
        <Box textAlign="center" width="100%">
          <Text as="div" variant="m" style={{ opacity: 0.9, marginBottom: '1rem', color: Palette.white }}>
            Go ahead, I’m listening
          </Text>
          <Orb aria-hidden="true" $isRecording={isRecording} />
          <Text as="div" variant="xl" style={{ marginTop: '2rem', color: Palette.white }}>
          </Text>
        </Box>
      </Center>

      <BottomBar>
        <MicButton
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
          onClick={toggleRecording}
          data-testid="voice-microphone-button"
        >
          {isRecording ? (
            <Stop size={32} color={Palette.royalBlue as unknown as string} />
          ) : (
            <Microphone size={32} color={Palette.royalBlue as unknown as string} />
          )}
        </MicButton>
      </BottomBar>
    </Overlay>
  );
};

export default VoiceAgentScreen;


